// git-push-command-classifier.ts
//
// Phân loại một dòng lệnh shell: nó có THỰC SỰ chạy `git push` không?
//
// Vì sao không grep chuỗi "push" (S3b, test 56-58):
//   • `git log --grep push`          → chỉ TÌM chữ push, không đẩy gì cả.
//   • `git commit -m "a; git push"`  → dấu `;` NẰM TRONG chuỗi trích dẫn ⇒ không phải
//                                      dấu phân tách lệnh; đây là false positive kinh điển.
//   • `git push origin main`         → mới là push thật.
//
// Cách làm: tokenize có hiểu dấu nháy (đơn/kép, escape), CẮT theo dấu phân tách
// THẬT (`;` `&&` `||` `|` `&` nằm NGOÀI nháy), rồi mới xét từng segment.
//
// Fail-closed: chuỗi nháy không đóng (không tokenize nổi) ⇒ coi như CÓ push, để tầng
// policy chặn thay vì cho lọt.

export interface PushClassification {
  isPush: boolean;
  reason: string;
  /** Segment (đã tách) chứa lệnh push, nếu có. */
  matchedSegment?: string;
}

interface Token {
  value: string;
  /** true khi token nằm trong dấu nháy ⇒ KHÔNG bao giờ là toán tử shell. */
  quoted: boolean;
}

const SEPARATORS = new Set([";", "&&", "||", "|", "&", "\n"]);

/** Tokenize thô nhưng đủ: phân biệt nội dung trong nháy với toán tử ngoài nháy. */
function tokenize(command: string): Token[] | undefined {
  const tokens: Token[] = [];
  let buffer = "";
  let bufferQuoted = false;
  let quote: '"' | "'" | undefined;

  const flush = (): void => {
    if (buffer.length > 0) {
      tokens.push({ value: buffer, quoted: bufferQuoted });
      buffer = "";
      bufferQuoted = false;
    }
  };

  for (let i = 0; i < command.length; i += 1) {
    const ch = command[i] as string;

    if (quote !== undefined) {
      if (ch === "\\" && quote === '"' && i + 1 < command.length) {
        buffer += command[i + 1];
        i += 1;
        continue;
      }
      if (ch === quote) {
        quote = undefined;
        bufferQuoted = true;
        continue;
      }
      buffer += ch;
      continue;
    }

    if (ch === '"' || ch === "'") {
      quote = ch;
      bufferQuoted = true;
      continue;
    }
    if (ch === "\\" && i + 1 < command.length) {
      buffer += command[i + 1];
      i += 1;
      continue;
    }
    if (ch === " " || ch === "\t") {
      flush();
      continue;
    }
    // Toán tử NGOÀI nháy: tách thành token riêng.
    const two = command.slice(i, i + 2);
    if (two === "&&" || two === "||") {
      flush();
      tokens.push({ value: two, quoted: false });
      i += 1;
      continue;
    }
    if (ch === ";" || ch === "|" || ch === "&" || ch === "\n") {
      flush();
      tokens.push({ value: ch, quoted: false });
      continue;
    }
    buffer += ch;
  }

  if (quote !== undefined) return undefined; // nháy chưa đóng
  flush();
  return tokens;
}

function splitSegments(tokens: Token[]): Token[][] {
  const segments: Token[][] = [];
  let current: Token[] = [];
  for (const token of tokens) {
    if (!token.quoted && SEPARATORS.has(token.value)) {
      if (current.length > 0) segments.push(current);
      current = [];
      continue;
    }
    current.push(token);
  }
  if (current.length > 0) segments.push(current);
  return segments;
}

/** Cờ của `git` (trước subcommand) có mang GIÁ TRỊ đi kèm ⇒ phải bỏ qua cả giá trị. */
const GIT_FLAGS_WITH_VALUE = new Set(["-C", "-c", "--git-dir", "--work-tree", "--namespace"]);

function segmentIsPush(segment: Token[]): boolean {
  const words = segment.filter((t) => t.value.length > 0);
  if (words.length === 0) return false;

  // Bỏ tiền tố gán biến môi trường (FOO=bar git push ...).
  let index = 0;
  while (index < words.length && /^[A-Za-z_][A-Za-z0-9_]*=/.test(words[index]?.value ?? "")) {
    index += 1;
  }
  // Bỏ các wrapper thường gặp.
  while (
    index < words.length &&
    ["env", "sudo", "command", "nice"].includes(words[index]?.value ?? "")
  ) {
    index += 1;
  }

  const binary = words[index]?.value ?? "";
  if (!/(^|\/)git$/.test(binary)) return false;
  index += 1;

  // Bỏ cờ toàn cục của git để tìm SUBCOMMAND thật.
  while (index < words.length) {
    const word = words[index]?.value ?? "";
    if (GIT_FLAGS_WITH_VALUE.has(word)) {
      index += 2;
      continue;
    }
    if (word.startsWith("-")) {
      index += 1;
      continue;
    }
    break;
  }

  const subcommand = words[index]?.value;
  return subcommand === "push";
}

/**
 * `git push` có thực sự được chạy không. Token nằm TRONG dấu nháy không bao giờ được
 * coi là lệnh (đó chỉ là dữ liệu, ví dụ nội dung commit message).
 */
export function classifyPushCommand(command: string): PushClassification {
  const tokens = tokenize(command);
  if (tokens === undefined) {
    return {
      isPush: true,
      reason:
        "Không tokenize được (dấu nháy chưa đóng) ⇒ fail-closed: coi như có push để policy chặn.",
    };
  }

  for (const segment of splitSegments(tokens)) {
    if (segmentIsPush(segment)) {
      return {
        isPush: true,
        reason: "Segment chạy `git push` thật.",
        matchedSegment: segment.map((t) => t.value).join(" "),
      };
    }
  }

  return {
    isPush: false,
    reason: command.includes("push")
      ? "Có chữ 'push' nhưng không segment nào THỰC SỰ chạy `git push` (chuỗi trích dẫn/tham số grep)."
      : "Không có `git push`.",
  };
}
